import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';

import { ApiService } from '../../../shared/services/api.service';
import { PROFILE_COPY } from '../profile.copy';

/**
 * Forma exacta del payload (D16 de `perron-coins-plan.md`): `PC1` + `user_id` en 8 dígitos + nonce de 6 `[A-Z0-9]` +
 * firma de 24 hex en mayúsculas = 41 caracteres, solo `[A-Z0-9]` (modo alfanumérico del QR).
 */
export const PROFILE_QR_PAYLOAD = /^PC1\d{8}[A-Z0-9]{6}[0-9A-F]{24}$/;

export type ProfileQrErrorReason = 'clients-only' | 'rate-limited' | 'invalid' | 'unknown';

/** Error de dominio del QR: `reason` decide el estado y `message` es el texto que se pinta. */
export class ProfileQrError extends Error {
  constructor(
    public readonly reason: ProfileQrErrorReason,
    message: string,
  ) {
    super(message);
    this.name = 'ProfileQrError';
  }
}

interface ProfileQrResponse {
  payload?: unknown;
}

const QR_COPY = PROFILE_COPY.COINS.QR;

/**
 * Cliente de «Mi QR Perrón» (A1·web, D20-D21): `GET /perron-coins/qr/me` y `POST /perron-coins/qr/me/regenerate`. Los
 * dos devuelven solo la cadena firmada; la web dibuja el QR. Un payload que no casa con D16 es un error (`invalid`):
 * nunca se dibuja un QR que el TPV no podría resolver.
 *
 * Sin toast: el componente decide qué pinta. El payload no se registra en ningún sitio (Q9).
 */
@Injectable({ providedIn: 'root' })
export class ProfileQrService {
  private readonly api = inject(ApiService);

  me(): Observable<string> {
    return this.api.get<ProfileQrResponse>('/perron-coins/qr/me').pipe(
      map(toPayload),
      catchError((err: unknown) => mapError(err)),
    );
  }

  /** Versión nueva del QR: el anterior deja de resolver en el TPV. */
  regenerate(): Observable<string> {
    return this.api.post<ProfileQrResponse>('/perron-coins/qr/me/regenerate', {}).pipe(
      map(toPayload),
      catchError((err: unknown) => mapError(err)),
    );
  }
}

function toPayload(res: ProfileQrResponse | null | undefined): string {
  const payload = res?.payload;
  if (typeof payload !== 'string' || !PROFILE_QR_PAYLOAD.test(payload)) {
    throw new ProfileQrError('invalid', QR_COPY.LOAD_ERROR);
  }
  return payload;
}

/** 403 → solo clientes; 429 → el texto del servidor (o el espejo); el resto → `unknown`. Patrón de `PointsService`. */
function mapError(err: unknown): Observable<never> {
  if (err instanceof ProfileQrError) return throwError(() => err);
  if (!(err instanceof HttpErrorResponse)) {
    return throwError(() => new ProfileQrError('unknown', QR_COPY.LOAD_ERROR));
  }
  if (err.status === 403) {
    return throwError(() => new ProfileQrError('clients-only', QR_COPY.CLIENTS_ONLY));
  }
  if (err.status === 429) {
    const text: unknown = err.error?.error;
    const message = typeof text === 'string' && text.trim() !== '' ? text : QR_COPY.REGENERATE_RATE_LIMITED;
    return throwError(() => new ProfileQrError('rate-limited', message));
  }
  return throwError(() => new ProfileQrError('unknown', QR_COPY.LOAD_ERROR));
}
